import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Target,
  Globe,
  Search,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Lightbulb,
  ListTodo,
  FolderKanban
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppChat from "@/components/WhatsAppChat";
import GSCConnect from "@/components/GSCConnect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/seo-dashboard`;

export default function SEODashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [rankings, setRankings] = useState(null);
  const [issues, setIssues] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [gscConnected, setGscConnected] = useState(false);
  
  // Project form
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDomain, setNewProjectDomain] = useState("");
  const [newProjectKeywords, setNewProjectKeywords] = useState("");
  
  // Task form
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDesc, setNewTaskDesc] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Get user from context
  const { user, isLoggedIn, loading: userLoading } = useUser();

  useEffect(() => {
    if (!userLoading && isLoggedIn && user?.user_id) {
      fetchDashboardData();
    } else if (!userLoading && !isLoggedIn) {
      setLoading(false);
    }
  }, [user, isLoggedIn, userLoading]);

  const fetchDashboardData = async () => {
    if (!user?.user_id) return;
    
    setLoading(true);
    try {
      const userId = user.user_id;
      const [overviewRes, projectsRes, rankingsRes, issuesRes, recsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/overview?user_id=${userId}`),
        axios.get(`${API}/projects/list?user_id=${userId}`),
        axios.get(`${API}/rankings/summary?user_id=${userId}`),
        axios.get(`${API}/issues/summary?user_id=${userId}`),
        axios.get(`${API}/recommendations?user_id=${userId}`),
        axios.get(`${API}/tasks/list?user_id=${userId}`)
      ]);
      
      setOverview(overviewRes.data);
      setProjects(projectsRes.data.projects || []);
      setRankings(rankingsRes.data);
      setIssues(issuesRes.data);
      setRecommendations(recsRes.data);
      setTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!newProjectName.trim() || !newProjectDomain.trim()) {
      toast.error("Please enter project name and domain");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/projects/create`, {
        name: newProjectName,
        domain: newProjectDomain,
        target_keywords: newProjectKeywords.split('\n').filter(k => k.trim())
      });
      
      setProjects([response.data.project, ...projects]);
      setShowNewProject(false);
      setNewProjectName("");
      setNewProjectDomain("");
      setNewProjectKeywords("");
      toast.success("Project created!");
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const createTask = async () => {
    if (!newTaskTitle.trim() || !selectedProjectId) {
      toast.error("Please select project and enter task title");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/create`, {
        project_id: selectedProjectId,
        title: newTaskTitle,
        description: newTaskDesc,
        priority: newTaskPriority
      });
      
      setTasks([response.data.task, ...tasks]);
      setShowNewTask(false);
      setNewTaskTitle("");
      setNewTaskDesc("");
      toast.success("Task created!");
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`);
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: "completed" } : t));
      toast.success("Task completed!");
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading || userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <Search className="w-12 h-12 mx-auto mb-4 text-violet-600" />
            <h2 className="text-xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4">Please login to access your SEO Dashboard</p>
            <Button onClick={() => navigate("/auth?redirect=/tools/seo/dashboard")} className="bg-violet-600 hover:bg-violet-700">
              Sign In
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-2xl mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">
              <FolderKanban className="w-4 h-4 mr-2" /> Projects
            </TabsTrigger>
            <TabsTrigger value="rankings" data-testid="tab-rankings">
              <TrendingUp className="w-4 h-4 mr-2" /> Rankings
            </TabsTrigger>
            <TabsTrigger value="issues" data-testid="tab-issues">
              <AlertTriangle className="w-4 h-4 mr-2" /> Issues
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              <ListTodo className="w-4 h-4 mr-2" /> Tasks
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4" data-testid="metric-projects">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Projects</p>
                      <p className="text-3xl font-bold text-violet-600">{overview?.overview?.total_projects || 0}</p>
                    </div>
                    <FolderKanban className="w-10 h-10 text-violet-200" />
                  </div>
                </Card>
                
                <Card className="p-4" data-testid="metric-health">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Health Score</p>
                      <p className={`text-3xl font-bold ${getScoreColor(overview?.overview?.avg_health_score || 0)}`}>
                        {overview?.overview?.avg_health_score || 0}
                      </p>
                    </div>
                    <Target className="w-10 h-10 text-green-200" />
                  </div>
                </Card>
                
                <Card className="p-4" data-testid="metric-position">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Position</p>
                      <p className="text-3xl font-bold text-blue-600">#{Math.round(overview?.overview?.avg_ranking_position || 0)}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-blue-200" />
                  </div>
                </Card>
                
                <Card className="p-4" data-testid="metric-tasks">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Pending Tasks</p>
                      <p className="text-3xl font-bold text-orange-600">{overview?.overview?.pending_tasks || 0}</p>
                    </div>
                    <ListTodo className="w-10 h-10 text-orange-200" />
                  </div>
                </Card>
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Search className="w-5 h-5" /> Quick Stats
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Sites Analyzed</span>
                      <Badge variant="secondary">{overview?.quick_stats?.sites_analyzed || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Keywords Tracked</span>
                      <Badge variant="secondary">{overview?.overview?.total_keywords_tracked || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Issues Found</span>
                      <Badge variant="destructive">{overview?.quick_stats?.issues_found || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Completed Tasks</span>
                      <Badge className="bg-green-100 text-green-800">{overview?.overview?.completed_tasks || 0}</Badge>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" /> Top Recommendations
                  </h3>
                  <div className="space-y-3">
                    {recommendations?.recommendations?.slice(0, 4).map((rec, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                        rec.priority === 'critical' ? 'border-red-500 bg-red-50' :
                        rec.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                        'border-blue-500 bg-blue-50'
                      }`}>
                        <p className="font-medium text-sm">{rec.title}</p>
                        <p className="text-xs text-gray-600 mt-1">{rec.action}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Recent Reports */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Recent Site Audits</h3>
                {overview?.recent_reports?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Domain</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Overall</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">SEO</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Performance</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Technical</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Issues</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {overview.recent_reports.map((report, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium">{report.domain}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-lg font-bold ${getScoreColor(report.overall_score)}`}>
                                {report.overall_score}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">{report.seo_score}</td>
                            <td className="px-4 py-3 text-center">{report.performance_score}</td>
                            <td className="px-4 py-3 text-center">{report.technical_score}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant={report.issues?.length > 5 ? "destructive" : "secondary"}>
                                {report.issues?.length || 0}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No site audits yet. Go to SEO Tools to analyze your first site!</p>
                    <Button className="mt-4" onClick={() => navigate("/tools/seo")}>
                      Start Site Audit
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">SEO Projects</h2>
                <Button onClick={() => setShowNewProject(true)} data-testid="new-project-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Project
                </Button>
              </div>

              {showNewProject && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Create New Project</h3>
                  <div className="space-y-4">
                    <Input
                      placeholder="Project Name (e.g., My E-commerce Site)"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      data-testid="project-name-input"
                    />
                    <Input
                      placeholder="Domain (e.g., example.com)"
                      value={newProjectDomain}
                      onChange={(e) => setNewProjectDomain(e.target.value)}
                      data-testid="project-domain-input"
                    />
                    <textarea
                      className="w-full min-h-[100px] p-3 border rounded-md"
                      placeholder="Target keywords (one per line)"
                      value={newProjectKeywords}
                      onChange={(e) => setNewProjectKeywords(e.target.value)}
                      data-testid="project-keywords-input"
                    />
                    <div className="flex gap-2">
                      <Button onClick={createProject} data-testid="create-project-btn">Create Project</Button>
                      <Button variant="outline" onClick={() => setShowNewProject(false)}>Cancel</Button>
                    </div>
                  </div>
                </Card>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <Card key={project.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`project-${project.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{project.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {project.domain}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteProject(project.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Health Score</span>
                        <span className={`font-bold ${getScoreColor(project.health_score || 0)}`}>
                          {project.health_score || 'N/A'}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Target Keywords</p>
                        <div className="flex flex-wrap gap-1">
                          {project.target_keywords?.slice(0, 3).map((kw, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                          ))}
                          {project.target_keywords?.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{project.target_keywords.length - 3} more</Badge>
                          )}
                        </div>
                      </div>
                      
                      <Button className="w-full mt-4" variant="outline" onClick={() => navigate(`/tools/seo?domain=${project.domain}`)}>
                        <Search className="w-4 h-4 mr-2" /> Analyze Site
                      </Button>
                    </div>
                  </Card>
                ))}
                
                {projects.length === 0 && (
                  <Card className="col-span-full p-12 text-center">
                    <FolderKanban className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
                    <p className="text-gray-500 mb-4">Create your first SEO project to start tracking</p>
                    <Button onClick={() => setShowNewProject(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Create Project
                    </Button>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Keyword Rankings</h2>
              
              {/* GSC Connect Card */}
              <GSCConnect 
                onConnect={() => setGscConnected(true)} 
                onDisconnect={() => setGscConnected(false)} 
              />

              {/* Rankings Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600">Total Keywords</p>
                  <p className="text-3xl font-bold text-violet-600">{rankings?.total_keywords || 0}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600">Avg Position</p>
                  <p className="text-3xl font-bold text-blue-600">#{rankings?.avg_position || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-green-50">
                  <p className="text-sm text-gray-600">Top 10</p>
                  <p className="text-3xl font-bold text-green-600">{rankings?.top_10 || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-blue-50">
                  <p className="text-sm text-gray-600">Top 30</p>
                  <p className="text-3xl font-bold text-blue-600">{rankings?.top_30 || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-orange-50">
                  <p className="text-sm text-gray-600">Top 100</p>
                  <p className="text-3xl font-bold text-orange-600">{rankings?.top_100 || 0}</p>
                </Card>
              </div>

              {/* By Domain */}
              {rankings?.by_domain?.length > 0 ? (
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Rankings by Domain</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Domain</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Keywords</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Avg Position</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Top 10</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {rankings.by_domain.map((domain, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{domain.domain}</td>
                            <td className="px-4 py-3 text-center">{domain.keywords}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={domain.avg_position <= 10 ? 'bg-green-100 text-green-800' : domain.avg_position <= 30 ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}>
                                #{domain.avg_position}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">{domain.top_10}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ) : (
                <Card className="p-12 text-center">
                  <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No rankings tracked yet</h3>
                  <p className="text-gray-500 mb-4">Go to SEO Tools &gt; Rank Tracking to start tracking keywords</p>
                  <Button onClick={() => navigate("/tools/seo")}>
                    Start Tracking
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">SEO Issues</h2>

              {/* Issues Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600">Total Issues</p>
                  <p className="text-3xl font-bold text-gray-900">{issues?.total_issues || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-red-50">
                  <p className="text-sm text-gray-600">High Priority</p>
                  <p className="text-3xl font-bold text-red-600">{issues?.by_severity?.high || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-orange-50">
                  <p className="text-sm text-gray-600">Medium Priority</p>
                  <p className="text-3xl font-bold text-orange-600">{issues?.by_severity?.medium || 0}</p>
                </Card>
                <Card className="p-4 text-center bg-yellow-50">
                  <p className="text-sm text-gray-600">Low Priority</p>
                  <p className="text-3xl font-bold text-yellow-600">{issues?.by_severity?.low || 0}</p>
                </Card>
              </div>

              {/* Issues by Category */}
              {issues?.by_category && Object.keys(issues.by_category).length > 0 && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Issues by Category</h3>
                  <div className="space-y-3">
                    {Object.entries(issues.by_category).map(([cat, count]) => (
                      <div key={cat} className="flex items-center gap-4">
                        <span className="w-32 text-sm">{cat}</span>
                        <Progress value={(count / issues.total_issues) * 100} className="flex-1" />
                        <span className="text-sm font-medium w-12">{count}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Recent Issues */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Recent Issues</h3>
                {issues?.recent_issues?.length > 0 ? (
                  <div className="space-y-3">
                    {issues.recent_issues.slice(0, 10).map((issue, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                        issue.severity === 'high' ? 'border-red-500 bg-red-50' :
                        issue.severity === 'medium' ? 'border-orange-500 bg-orange-50' :
                        'border-yellow-500 bg-yellow-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{issue.issue}</p>
                            <p className="text-sm text-gray-600 mt-1">{issue.recommendation}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">{issue.category}</Badge>
                              <Badge variant="outline">{issue.domain}</Badge>
                            </div>
                          </div>
                          <Badge className={
                            issue.severity === 'high' ? 'bg-red-100 text-red-800' :
                            issue.severity === 'medium' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {issue.severity}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-16 h-16 mx-auto text-green-300 mb-4" />
                    <p>No issues found! Your sites are healthy.</p>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">SEO Tasks</h2>
                <Button onClick={() => setShowNewTask(true)} disabled={projects.length === 0} data-testid="new-task-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Task
                </Button>
              </div>

              {showNewTask && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Create New Task</h3>
                  <div className="space-y-4">
                    <select
                      className="w-full p-2 border rounded-md"
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      data-testid="task-project-select"
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <Input
                      placeholder="Task Title"
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      data-testid="task-title-input"
                    />
                    <textarea
                      className="w-full min-h-[80px] p-3 border rounded-md"
                      placeholder="Task Description"
                      value={newTaskDesc}
                      onChange={(e) => setNewTaskDesc(e.target.value)}
                    />
                    <select
                      className="w-full p-2 border rounded-md"
                      value={newTaskPriority}
                      onChange={(e) => setNewTaskPriority(e.target.value)}
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                      <option value="critical">Critical</option>
                    </select>
                    <div className="flex gap-2">
                      <Button onClick={createTask} data-testid="create-task-btn">Create Task</Button>
                      <Button variant="outline" onClick={() => setShowNewTask(false)}>Cancel</Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Pending Tasks */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Pending Tasks</h3>
                {tasks.filter(t => t.status !== 'completed').length > 0 ? (
                  <div className="space-y-3">
                    {tasks.filter(t => t.status !== 'completed').map((task) => (
                      <div key={task.id} className="p-4 border rounded-lg flex items-center justify-between hover:bg-gray-50" data-testid={`task-${task.id}`}>
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                            <Badge variant="outline">{task.category}</Badge>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => completeTask(task.id)} className="bg-green-600 hover:bg-green-700">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-green-300 mb-4" />
                    <p>All tasks completed! Great work!</p>
                  </div>
                )}
              </Card>

              {/* Completed Tasks */}
              {tasks.filter(t => t.status === 'completed').length > 0 && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 text-gray-600">Completed Tasks</h3>
                  <div className="space-y-2">
                    {tasks.filter(t => t.status === 'completed').slice(0, 5).map((task) => (
                      <div key={task.id} className="p-3 border rounded-lg bg-gray-50 flex items-center gap-3 opacity-60">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="line-through">{task.title}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
      <WhatsAppChat />
    </div>
  );
}
